import { Link } from "react-router-dom";
import {
  Server, Globe, HardDrive, Shield, Zap, Clock, ArrowRight, CheckCircle2,
  Layers, Infinity, Briefcase,
} from "lucide-react";

const hostingTypes = [
  {
    id: "linux",
    icon: Server,
    title: "Linux Hosting",
    description: "cPanel ve LiteSpeed tabanlı yüksek performanslı Linux hosting. PHP, MySQL, Node.js ve daha fazlasını anında çalıştırın.",
    bullets: ["cPanel kontrol paneli", "PHP 7.x - 8.x çoklu sürüm", "MySQL / MariaDB", "SSH erişimi"],
  },
  {
    id: "windows",
    icon: Layers,
    title: "Windows Hosting",
    description: ".NET, ASP.NET Core ve MS SQL destekli Plesk tabanlı Windows sunuculu hosting.",
    bullets: ["Plesk paneli", "ASP.NET & .NET Core", "MS SQL veritabanı", "Windows Server 2022"],
  },
  {
    id: "wordpress",
    icon: Globe,
    title: "WordPress Hosting",
    description: "Tek tıkla kurulum, otomatik güncelleme ve özel WordPress cache ile optimize edilmiş paketler.",
    bullets: ["Tek tık kurulum", "WP-CLI desteği", "Yönetilen güncellemeler", "Özel WP cache"],
  },
  {
    id: "unlimited",
    icon: Infinity,
    title: "Sınırsız Hosting",
    description: "Sınırsız site, sınırsız e-posta, sınırsız veritabanı — adil kullanım politikasıyla.",
    bullets: ["Sınırsız alan", "Sınırsız site", "Sınırsız e-posta", "Sınırsız MySQL"],
  },
  {
    id: "pro",
    icon: Briefcase,
    title: "Profesyonel Hosting",
    description: "Kurumsal ihtiyaçlar için ayrılmış kaynaklar, öncelikli destek ve SLA garantisi.",
    bullets: ["Ayrılmış CPU/RAM", "Öncelikli destek", "99,99 SLA", "Günlük yedekler"],
  },
];

const commonFeatures = [
  { icon: HardDrive, t: "SSD / NVMe Depolama", d: "SATA'dan 5-10 kat daha hızlı." },
  { icon: Shield, t: "Ücretsiz SSL", d: "Let's Encrypt otomatik kurulum." },
  { icon: Clock, t: "Otomatik Yedekleme", d: "Her gece tam site yedeği." },
  { icon: Zap, t: "cPanel / Plesk", d: "Tanıdık, hızlı kontrol panelleri." },
];

export default function Hosting() {
  return (
    <div data-testid="hosting-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-50 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-96 radial-glow-blue pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-20 lg:pt-24 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-5">Hosting Hizmetleri</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] max-w-4xl mx-auto">
            Her proje için <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">doğru hosting.</span>
          </h1>
          <p className="mt-8 text-lg text-zinc-400 max-w-2xl mx-auto">
            Kişisel blogdan kurumsal e-ticarete — Linux, Windows ve WordPress için optimize
            edilmiş SSD / NVMe altyapısı.
          </p>
        </div>
      </section>

      {/* Common features */}
      <section className="relative py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-5">
          {commonFeatures.map((f, i) => (
            <div key={i} data-testid={`hosting-feature-${i}`} className="glass rounded-2xl p-6 card-hover">
              <f.icon size={20} strokeWidth={1.6} className="text-blue-400 mb-4" />
              <div className="font-heading font-semibold mb-1">{f.t}</div>
              <div className="text-xs text-zinc-500 leading-relaxed">{f.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Hosting types */}
      <section className="relative py-20 space-y-16">
        {hostingTypes.map((h, idx) => (
          <div key={h.id} id={h.id} data-testid={`hosting-type-${h.id}`} className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className={`grid lg:grid-cols-5 gap-10 items-start ${idx % 2 === 1 ? "" : ""}`}>
              <div className="lg:col-span-2">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/5 px-3 py-1 mb-5">
                  <h.icon size={14} className="text-blue-400" />
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-400">Paket {idx + 1}</span>
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mb-4">{h.title}</h2>
                <p className="text-zinc-400 leading-relaxed mb-8">{h.description}</p>
                <Link to="/fiyatlandirma" data-testid={`hosting-cta-${h.id}`} className="btn-shine inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-6 py-3 text-sm font-semibold text-white transition-all">
                  Paketleri gör <ArrowRight size={14} />
                </Link>
              </div>
              <div className="lg:col-span-3 glass rounded-3xl p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {h.bullets.map((b) => (
                    <div key={b} className="flex items-start gap-3 rounded-xl bg-white/[0.02] border border-white/5 p-4">
                      <CheckCircle2 size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-zinc-200">{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="relative py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight mb-6">
            Hangi paket size uygun?
          </h2>
          <p className="text-zinc-400 mb-10 max-w-xl mx-auto">
            Paketleri karşılaştırın veya ekibimize danışın — 30 dakika içinde ideal çözümü belirleyin.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link to="/fiyatlandirma" data-testid="hosting-final-cta-pricing" className="btn-shine inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-4 text-sm font-semibold text-white transition-all">
              Fiyatları gör <ArrowRight size={15} />
            </Link>
            <Link to="/iletisim" data-testid="hosting-final-cta-contact" className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-white/30 bg-white/5 hover:bg-white/10 px-7 py-4 text-sm font-semibold text-white transition-all">
              Bize ulaş
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
